import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import PageBanner from "@/components/PageBanner";
import PaymentPartners from "@/components/PaymentPartners";
import { Car, Shield, MapPin, PhoneCall, Users, Wrench } from "lucide-react";

const features = [
  {
    icon: Car,
    title: "Professional Drivers",
    description: "Trained, certified, and experienced drivers with extensive knowledge of East African roads"
  },
  {
    icon: Shield,
    title: "Insured Vehicles",
    description: "All vehicles are fully insured for your safety and peace of mind"
  },
  {
    icon: MapPin,
    title: "Extensive Coverage",
    description: "100+ km of tarmacked roads connecting major towns and cities in Uganda, Kenya, Tanzania, and Rwanda"
  },
  {
    icon: PhoneCall,
    title: "24/7 Support",
    description: "Round-the-clock roadside assistance and customer support"
  },
  {
    icon: Users,
    title: "Group Transport",
    description: "Coaches and buses available for larger groups and tours"
  },
  {
    icon: Wrench,
    title: "Well Maintained",
    description: "Regular maintenance and inspections ensure vehicle reliability"
  }
];

const vehicles = [
  "/car1.png",
  "/car2.png",
  "/car3.png",
  "/car4.png",
  "/car5.png",
  "/car6.png",
  "/car7.png",
  "/car.png"
];

export default function CarHireServices() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Car Hire Services | Ecoplace Tours</title>
        <meta
          name="description"
          content="Explore East Africa with our comfortable, insured, and well-maintained vehicles. Professional drivers and 24/7 support available."
        />
      </Helmet>

      <PageBanner
        title="Car Hire Services"
        description="Explore East Africa at your own pace with our comfortable, insured, and well-maintained vehicles."
        backgroundImage="/car6.png"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 sm:p-8"
            >
              <div className="flex items-center gap-4 mb-4">
                <feature.icon className="w-8 h-8 text-primary" />
                <h3 className="text-xl font-semibold text-gray-900">
                  {feature.title}
                </h3>
              </div>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Vehicle Gallery Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-24"
        >
          <h2 className="text-3xl font-bold text-center mb-12">Our Fleet</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {vehicles.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="aspect-square relative rounded-lg overflow-hidden bg-white shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <img
                  src={image}
                  alt={`Vehicle ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
      
      <PaymentPartners />
    </div>
  );
}